import React, { useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { TrinitiDatePicker } from '@/components/triniti-datetime';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Activity, Plus, Lock, Pencil } from 'lucide-react';

const CATEGORIES = ['CHRONIC', 'ACUTE', 'MENTAL_HEALTH', 'SENSORY', 'MOBILITY', 'OTHER'];
const STATUSES = ['ACTIVE', 'IN_REMISSION', 'RESOLVED', 'SUSPECTED'];
const SEVERITIES = ['LOW', 'MODERATE', 'HIGH'];

const SEV_BADGE = {
  HIGH: 'bg-red-100 text-red-800 border-red-300',
  MODERATE: 'bg-amber-100 text-amber-800 border-amber-300',
  LOW: 'bg-muted text-muted-foreground border-muted',
};

const STATUS_BADGE = {
  ACTIVE: 'bg-blue-50 text-blue-700 border-blue-300',
  IN_REMISSION: 'bg-emerald-50 text-emerald-700 border-emerald-300',
  RESOLVED: 'bg-muted text-muted-foreground border-muted',
  SUSPECTED: 'bg-amber-50 text-amber-700 border-amber-300',
};

const EMPTY_FORM = {
  condition_name: '', icd_code: '', category: 'CHRONIC', status: 'ACTIVE', severity: 'MODERATE',
  diagnosed_date: '', diagnosed_by: '', notes: '',
};

export function HealthConditionsTab({ participantId, canManage }) {
  const [enabled, setEnabled] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const fr = await api.get(`/participants/${participantId}/health-conditions/feature-flag`);
      const isOn = !!fr.data?.enabled;
      setEnabled(isOn);
      if (!isOn) return;
      const r = await api.get(`/participants/${participantId}/health-conditions`);
      setItems(r.data?.items || []);
    } catch (_e) {
      setEnabled(false);
    } finally { setLoading(false); }
  }, [participantId]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setForm(EMPTY_FORM); setDialogOpen(true); };
  const openEdit = (row) => {
    setEditing(row);
    setForm({
      condition_name: row.condition_name || '',
      icd_code: row.icd_code || '',
      category: row.category || 'CHRONIC',
      status: row.status || 'ACTIVE',
      severity: row.severity || 'MODERATE',
      diagnosed_date: row.diagnosed_date || '',
      diagnosed_by: row.diagnosed_by || '',
      notes: row.notes || '',
    });
    setDialogOpen(true);
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    try {
      const payload = {
        ...form,
        icd_code: form.icd_code || null,
        diagnosed_date: form.diagnosed_date || null,
        diagnosed_by: form.diagnosed_by || null,
        notes: form.notes || null,
      };
      if (editing) {
        await api.put(`/participants/${participantId}/health-conditions/${editing.id}`, payload);
        toast.success('Condition updated');
      } else {
        await api.post(`/participants/${participantId}/health-conditions`, payload);
        toast.success('Condition added');
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      const msg = err?.response?.data?.error?.message || err?.response?.data?.detail?.message || err?.message || 'Save failed';
      toast.error(msg);
    }
  };

  const toggleActive = async (row) => {
    try {
      const action = row.is_active ? 'deactivate' : 'reactivate';
      await api.post(`/participants/${participantId}/health-conditions/${row.id}/${action}`);
      toast.success(row.is_active ? 'Condition deactivated' : 'Condition reactivated');
      load();
    } catch (err) { toast.error('Action failed'); }
  };

  if (loading) return <p className="text-muted-foreground py-4" data-testid="health-conditions-loading">Loading health conditions…</p>;
  if (enabled === false) {
    return (
      <Card data-testid="health-conditions-disabled">
        <CardContent className="py-6 flex items-start gap-3">
          <Lock className="h-5 w-5 text-muted-foreground mt-0.5" />
          <div>
            <p className="font-medium">Health Conditions module not enabled</p>
            <p className="text-sm text-muted-foreground">Ask an administrator to enable <code className="text-xs bg-muted px-1 rounded">participant_health_conditions_enabled</code>.</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="health-conditions-list-tab">
      <Card>
        <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
          <div>
            <CardTitle className="text-base flex items-center gap-2"><Activity className="h-4 w-4 text-blue-600" /> Health Conditions</CardTitle>
            <p className="text-xs text-muted-foreground mt-1">Structured condition register. Active high/moderate conditions appear in the header chip row.</p>
          </div>
          {canManage && (
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button size="sm" onClick={openCreate} data-testid="health-conditions-add-button">
                  <Plus className="h-4 w-4 mr-1" /> Add Condition
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>{editing ? 'Edit Condition' : 'Add Health Condition'}</DialogTitle>
                  <DialogDescription>Record condition, category, status, and severity.</DialogDescription>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="col-span-2 space-y-1">
                      <Label>Condition Name</Label>
                      <Input value={form.condition_name} onChange={e => setForm({ ...form, condition_name: e.target.value })} required data-testid="health-conditions-form-name" />
                    </div>
                    <div className="space-y-1">
                      <Label>ICD Code</Label>
                      <Input value={form.icd_code} onChange={e => setForm({ ...form, icd_code: e.target.value })} placeholder="optional" />
                    </div>
                    <div className="space-y-1">
                      <Label>Category</Label>
                      <Select value={form.category} onValueChange={v => setForm({ ...form, category: v })}>
                        <SelectTrigger data-testid="health-conditions-form-category"><SelectValue /></SelectTrigger>
                        <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c.replace(/_/g, ' ')}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label>Status</Label>
                      <Select value={form.status} onValueChange={v => setForm({ ...form, status: v })}>
                        <SelectTrigger data-testid="health-conditions-form-status"><SelectValue /></SelectTrigger>
                        <SelectContent>{STATUSES.map(s => <SelectItem key={s} value={s}>{s.replace(/_/g, ' ')}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label>Severity</Label>
                      <Select value={form.severity} onValueChange={v => setForm({ ...form, severity: v })}>
                        <SelectTrigger data-testid="health-conditions-form-severity"><SelectValue /></SelectTrigger>
                        <SelectContent>{SEVERITIES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label>Diagnosed Date</Label>
                      <TrinitiDatePicker value={form.diagnosed_date || ''} onChange={v => setForm({ ...form, diagnosed_date: v })} testId="condition-diagnosed-date" />
                    </div>
                    <div className="space-y-1">
                      <Label>Diagnosed By</Label>
                      <Input value={form.diagnosed_by} onChange={e => setForm({ ...form, diagnosed_by: e.target.value })} placeholder="e.g. Dr Smith" />
                    </div>
                    <div className="col-span-2 space-y-1">
                      <Label>Notes (staff-only)</Label>
                      <Textarea rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Clinical commentary — not visible to Family Portal" />
                    </div>
                  </div>
                  <Button type="submit" className="w-full" data-testid="health-conditions-form-submit">{editing ? 'Save changes' : 'Add Condition'}</Button>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Condition</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Severity</TableHead>
                <TableHead>Diagnosed</TableHead>
                <TableHead>By</TableHead>
                <TableHead>Active</TableHead>
                {canManage && <TableHead>Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.length === 0 ? (
                <TableRow><TableCell colSpan={canManage ? 8 : 7} className="text-center py-8 text-muted-foreground" data-testid="health-conditions-empty-state">No conditions recorded.</TableCell></TableRow>
              ) : items.map(row => (
                <TableRow key={row.id} data-testid={`health-conditions-row-${row.id}`} data-condition-row={row.id} className={!row.is_active ? 'opacity-50' : ''}>
                  <TableCell className="font-medium" data-testid={`condition-row-${row.id}`}>{row.condition_name}{row.icd_code && <span className="text-[10px] text-muted-foreground ml-1">({row.icd_code})</span>}</TableCell>
                  <TableCell><Badge variant="outline" className="text-[10px]">{(row.category || '').replace(/_/g, ' ')}</Badge></TableCell>
                  <TableCell><Badge variant="outline" className={`text-[10px] ${STATUS_BADGE[row.status] || ''}`}>{(row.status || '').replace(/_/g, ' ')}</Badge></TableCell>
                  <TableCell><Badge variant="outline" className={`text-[10px] ${SEV_BADGE[row.severity] || ''}`}>{row.severity}</Badge></TableCell>
                  <TableCell className="font-mono text-xs">{row.diagnosed_date || '—'}</TableCell>
                  <TableCell className="text-sm">{row.diagnosed_by || '—'}</TableCell>
                  <TableCell>{row.is_active ? <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-300">Yes</Badge> : <Badge variant="outline" className="text-[10px]">No</Badge>}</TableCell>
                  {canManage && (
                    <TableCell className="space-x-1">
                      <Button size="sm" variant="outline" onClick={() => openEdit(row)} data-testid={`health-conditions-edit-${row.id}`} aria-label={`edit-condition-${row.id}`}><Pencil className="h-3 w-3" /></Button>
                      <Button
                        size="sm"
                        variant={row.is_active ? 'outline' : 'default'}
                        onClick={() => toggleActive(row)}
                        data-testid={row.is_active ? `health-conditions-deactivate-${row.id}` : `health-conditions-reactivate-${row.id}`}
                      >
                        {row.is_active ? 'Deactivate' : 'Reactivate'}
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

export default HealthConditionsTab;
